/**
 * F17 T17.1.5 — ParallelSplitNode: rozwidlenie procesu w gałęzie równoległe.
 *
 * Każda krawędź wychodząca tworzy osobną gałąź (process_step_instance per branch).
 * Wszystkie gałęzie muszą się zbiec w ParallelJoinNode żeby kontynuować.
 *
 * Handle layout: target top, 2 source handles bottom z różnymi `id` (path_1 / path_2).
 * Commit 3 freeze: 2 hard-coded ścieżki (positions 30% / 70%). Dynamic count
 * (3+ gałęzie) odłożone do Commit 6 razem z properties panel — admin wtedy
 * skonfiguruje liczbę gałęzi i handle pojawią się dynamicznie.
 *
 * `data.isCurrent` (F17 T17.1.8c) — podświetlenie w read-only `ProcessGraphViewer`. Builder
 * nigdy tego pola nie ustawia (patrz `graphTypes.ts`), więc render w builderze bez zmian.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { Split } from 'lucide-react';

import { CURRENT_NODE_RING_CLASS } from '@/lib/processes/step-tone';

interface ParallelSplitNodeData {
  isCurrent?: boolean;
  [key: string]: unknown;
}

export const ParallelSplitNode = memo(function ParallelSplitNode({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  const isCurrent = (data as ParallelSplitNodeData)?.isCurrent ?? false;
  return (
    <div
      className={`flex h-[50px] w-[100px] items-center justify-center rounded-lg bg-gray-600 shadow-lg transition-shadow dark:bg-gray-500 ${
        selected ? 'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-gray-900' : ''
      } ${isCurrent ? CURRENT_NODE_RING_CLASS : ''}`}
      data-testid="process-node-parallel-split"
      data-current={isCurrent ? 'true' : 'false'}
    >
      <Split className="h-5 w-5 text-white" aria-hidden="true" />
      <span className="ml-1 text-xs font-semibold text-white">
        {t('tenant.processes.builder.nodes.parallelSplit')}
      </span>
      {isCurrent && <span className="sr-only">{t('tenant.processes.flow.currentStepAria')}</span>}
      <Handle
        type="target"
        position={Position.Top}
        className="!h-3 !w-3 !border-2 !border-white !bg-gray-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="path_1"
        className="!left-[30%] !h-3 !w-3 !border-2 !border-white !bg-gray-500"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="path_2"
        className="!left-[70%] !h-3 !w-3 !border-2 !border-white !bg-gray-500"
      />
    </div>
  );
});
